import React, { useState } from "react";
import CommunityCardV2 from "../../components/Cards/CommunityCard/CommunityCardV2";
import { BodyContainer, Container } from "../../styles/ContainerStyles";
import HeaderComponent from "../../components/Header/Header";
import {
  useGetPaginatedCommunitiesCardsData,
  useDeleteCommunity,
  useAddCommunity,
  useUpdateCommunity,
} from "../../hooks/useCommunities";
import { CustomIcons } from "../../assets/index";
import Colors from "../../config/Colors";
import CommunitySettingsModal from "../../components/Modals/CommunitySettings";
import { Button, Input, Modal, message } from "antd";
import { toast } from "sonner";
import GenericDialog from "../../components/Dialog/GenericDialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import NewCommunityForm from "../../components/Forms/NewCommunity";
import { copyObjects } from "../../util/copyObjects";

const AllCommunities = () => {
  // const { isLoading, data: communities, isFetching } = useCommunities();
  const [currentCommunity, setCurrentCommunity] = useState<
    ICommunity | undefined
  >(undefined);
  const [currentId, setCurrentId] = useState<number | null>(null);

  const [openCommunityDrawer, setOpenCommunityDrawer] = useState(false);
  const [drawerMode, setDrawerMode] = useState<"create" | "edit">("create");
  const [openSettingsModal, setOpenSettingsModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const { Search } = Input;
  const [nextPage, setNextPage] = useState<number>(1);
  const [searchCommunity, setSearchCommunity] = useState<string | null>("");
  const limit = 6;

  const {
    isLoading,
    data: communityCard,
    refetch,
  } = useGetPaginatedCommunitiesCardsData(nextPage, limit, searchCommunity);

  const deleteCommunity = useDeleteCommunity();
  const addCommunity = useAddCommunity();
  const updateCommunity = useUpdateCommunity();
  const [messageApi, contextHolder] = message.useMessage();

  const handleCloseCommunityDrawer = () => {
    setOpenCommunityDrawer(false);
    setCurrentCommunity(undefined);
    addCommunity.reset();
    updateCommunity.reset();
  };

  const handleAddCommunity = async (community: IAddCommunity) => {
    try {
      messageApi.open({
        key: "addCommunity",
        type: "loading",
        content: "Creating Community...",
        duration: 0,
      });
      await addCommunity.mutateAsync(community);
      messageApi.open({
        key: "addCommunity",
        type: "success",
        content: "Community Created Successfully",
        duration: 2,
      });
      refetch();
      handleCloseCommunityDrawer();
    } catch (error) {
      messageApi.destroy("addCommunity");
      messageApi.error("Failed to create community");
    }
  };

  const handleEditCommunity = async (community: IAddCommunity) => {
    if (!currentCommunity) return;

    try {
      messageApi.open({
        key: "updateCommunity",
        type: "loading",
        content: "Updating Community...",
        duration: 0,
      });
      const editedCommunity = await copyObjects(currentCommunity, community);
      await updateCommunity.mutateAsync(editedCommunity);
      messageApi.open({
        key: "updateCommunity",
        type: "success",
        content: "Community Updated Successfully",
        duration: 2,
      });
      refetch();
      handleCloseCommunityDrawer();
    } catch (error) {
      messageApi.destroy("updateCommunity");
      messageApi.error("Failed to update community");
    }
  };

  const handleDeleteCommunity = async () => {
    if (!currentId) return;

    try {
      messageApi.open({
        key: "deleteCommunity",
        type: "loading",
        content: "Deleting Community...",
        duration: 0,
      });
      setOpenDeleteModal(false);
      await deleteCommunity.mutateAsync(currentId);
      messageApi.open({
        key: "deleteCommunity",
        type: "success",
        content: "Community Deleted Successfully",
        duration: 2,
      });
      refetch();
      setCurrentId(null);
      setCurrentCommunity(undefined);
    } catch (error) {
      messageApi.destroy("deleteCommunity");
      messageApi.error("Failed to delete community");
    }
  };

  const handleNextPage = () => {
    if (communityCard?.hasNextPage) {
      setNextPage(nextPage + 1);
      refetch();
    }
  };

  const handlePrevious = () => {
    if (communityCard?.hasPreviousPage) {
      setNextPage(nextPage - 1);
      refetch();
    }
  };

  const handleRefresh = () => {
    setSearchCommunity("");
    setNextPage(1);
    refetch();
  };

  const handleSearch = (value: string) => {
    if (value.length >= 3) {
      setNextPage(1);
      setSearchCommunity(value);
      refetch();
    } else {
      toast.error("Please enter at least 3 characters to search");
    }
  };

  const handleGoTo = (page: number) => {
    const totalPages = communityCard?.totalPages || 1;
    if (page >= 1 && page <= totalPages) {
      setNextPage(page);
      refetch();
    } else {
      toast.error(`Please enter a page number between 1 and ${totalPages}`);
    }
  };

  return (
    <div className="w-full h-full">
      {contextHolder}
      <Container className="w-full h-full">
        <BodyContainer className="w-full h-full">
          <HeaderComponent title="Communities" />
          {isLoading ? (
            <div className="fixed inset-0 flex items-center justify-center">
              <CustomIcons.EOSThreeDotsLoading
                width={80}
                height={80}
                viewBox={null}
                svgColor={Colors.appMainColor}
              />
            </div>
          ) : (
            <div className="mx-4">
              <div className="grid grid-cols-4 gap-4">
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "flex-end",
                    paddingRight: 10,
                    flexDirection: "row",
                    gap: 12,
                  }}
                >
                  {searchCommunity && searchCommunity.length > 0 && (
                    <button
                      type="button"
                      style={{ marginRight: 2 }}
                      onClick={handleRefresh}
                    >
                      Clear Search
                    </button>
                  )}

                  <Search
                    placeholder="Search for a Community"
                    allowClear
                    enterButton
                    size="large"
                    className="w-3"
                    onSearch={(value: string) => handleSearch(value)}
                  />

                  <Button
                    type="primary"
                    size="large"
                    className="ml-2w-2"
                    onClick={() => {
                      setDrawerMode("create");
                      setOpenCommunityDrawer(true);
                    }}
                  >
                    Create New Community
                  </Button>
                </div>
                {communityCard &&
                  Array.isArray(communityCard.data) &&
                  communityCard?.data?.map((card: ICommunity) => (
                    <CommunityCardV2
                      key={card.id}
                      communityData={{
                        id: card.id,
                        title: card.communityName,
                        subtitle: card.communityDescription,
                        imgSrc: card.communityIcon,
                      }}
                      editCard={() => {
                        setCurrentCommunity(card);
                        setDrawerMode("edit");
                        setOpenCommunityDrawer(true);
                      }}
                      openSettings={() => {
                        setCurrentId(card.id);
                        setOpenSettingsModal(true);
                      }}
                      deleteCommunity={() => {
                        setCurrentId(card.id);
                        setCurrentCommunity(card);
                        setOpenDeleteModal(true);
                      }}
                    />
                  ))}

                <div className="w-full flex flex-row pl-3 pr-3 justify-end items-center pb-2">
                  <Button
                    type="primary"
                    ghost
                    size="large"
                    className="w-1"
                    disabled={!communityCard?.hasPreviousPage}
                    onClick={handlePrevious}
                  >
                    Previous
                  </Button>
                  <div className="w-1/5 flex flex-row justify-content-center align-items-center gap-1">
                    <Input
                      size="middle"
                      style={{ width: 50, marginRight: 2, fontSize: "16px" }}
                      value={nextPage}
                      readOnly
                    />
                    <span style={{ fontSize: "17px" }}> Of </span>
                    <Input
                      size="middle"
                      style={{ width: 50, marginLeft: 2, fontSize: "16px" }}
                      value={
                        communityCard?.totalPages === 0
                          ? 1
                          : communityCard?.totalPages
                      }
                      readOnly
                    />
                    <span style={{ fontSize: "17px" }}> Go To </span>
                    <Input
                      size="middle"
                      style={{ width: 50, marginLeft: 2, fontSize: "16px" }}
                      onChange={(e) => handleGoTo(Number(e.target.value))}
                    />
                  </div>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    size="large"
                    className="w-1 bg-[#1757a2] shadow-none justify-center"
                    disabled={!communityCard?.hasNextPage}
                    onClick={handleNextPage}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </BodyContainer>
      </Container>
      {/* Community Create/Edit Drawer */}
      <Drawer open={openCommunityDrawer} onOpenChange={setOpenCommunityDrawer}>
        <DrawerContent className="w-1/2 max-w-none">
          <DrawerHeader>
            <DrawerTitle className="text-2xl font-bold">
              {drawerMode === "create"
                ? "Create New Community"
                : "Edit Community"}
            </DrawerTitle>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto px-4">
            <NewCommunityForm
              communityValues={
                drawerMode === "edit" ? currentCommunity : undefined
              }
              onFormFinish={(community: IAddCommunity) =>
                drawerMode === "create"
                  ? handleAddCommunity(community)
                  : handleEditCommunity(community)
              }
              onModelClose={handleCloseCommunityDrawer}
              isLoading={
                drawerMode === "create"
                  ? addCommunity.isPending
                  : updateCommunity.isPending
              }
              isSuccess={
                drawerMode === "create"
                  ? addCommunity.isSuccess
                  : updateCommunity.isSuccess
              }
            />
          </div>
        </DrawerContent>
      </Drawer>

      <CommunitySettingsModal
        communityId={currentId}
        openSettingsModal={openSettingsModal}
        setOpenSettingsModal={() => {
          setCurrentId(null);
          setOpenSettingsModal(false);
        }}
      />
      <Modal
        centered
        open={openDeleteModal}
        onOk={() => setOpenDeleteModal(false)}
        onCancel={() => setOpenDeleteModal(false)}
        width={550}
        closeIcon={
          <CustomIcons.CloseIcon
            width={30}
            height={30}
            viewBox={null}
            svgColor="#1C274C"
          />
        }
        footer={null}
      >
        <GenericDialog
          onClose={() => {
            setCurrentId(null);
            setCurrentCommunity(undefined);
            setOpenDeleteModal(false);
          }}
          onConfirm={handleDeleteCommunity}
          text={`delete ${currentCommunity?.communityName || "this community"}`}
        />
      </Modal>
    </div>
  );
};

export default AllCommunities;
